import { BigCheckbox, BigTextInput, FieldShell } from './FieldPrimitives';
import { CONSENT_CLAUSES } from '@/constants/intakeForm';
import TypedSignature from '@/components/TypedSignature';

// The Consent + Sign page. The patient types their initials ONCE at the top
// and checks an "I agree" box per clause. Submit-time we fan out that single
// initials string into all 12 consent_initials[key] values so the backend's
// REQUIRED_CONSENT_KEYS check still passes with one-and-done UX.
const ConsentSection = ({
  initials,
  setInitials,
  consents,
  setConsents,
  signedName,
  setSignedName,
  attested,
  setAttested,
  defaultName,
}) => {
  const agreedCount = CONSENT_CLAUSES.filter((c) => consents[c.key]).length;

  return (
    <div className="space-y-8">
      <p className="text-base md:text-lg text-gray-700">
        Read each item below carefully. Check the box next to each one to show
        that you have read and agree to it. You must agree to all twelve before
        you can submit.
      </p>

      <FieldShell
        label="Your initials"
        htmlFor="consent_initials"
        required
        help="Type your initials once here. For example, if your name is John Adam Smith, type JAS. We use this on the legal record next to each item below."
      >
        <BigTextInput
          id="consent_initials"
          value={initials}
          onChange={(v) =>
            setInitials(
              v
                .replace(/[^a-zA-Z]/g, '')
                .toUpperCase()
                .slice(0, 5),
            )
          }
          maxLength={5}
          required
          ariaInvalid={!initials || initials.length < 2}
        />
      </FieldShell>

      <div
        className="bg-mmj-lightblue text-mmj-header rounded-lg p-4 text-base md:text-lg font-semibold"
        aria-live="polite"
      >
        {agreedCount} of {CONSENT_CLAUSES.length} agreed
      </div>

      <div className="space-y-4">
        {CONSENT_CLAUSES.map((clause, idx) => (
          <div
            key={clause.key}
            className="border border-gray-200 rounded-xl p-4 md:p-5 bg-white"
          >
            <p className="text-base md:text-lg text-mmj-header leading-relaxed mb-4">
              <span className="font-bold mr-2">{idx + 1}.</span>
              {clause.text}
            </p>
            <BigCheckbox
              id={`consent_${clause.key}`}
              checked={!!consents[clause.key]}
              onChange={(v) => setConsents({ ...consents, [clause.key]: v })}
            >
              <span className="font-semibold">
                I have read and agree to the above
              </span>
            </BigCheckbox>
          </div>
        ))}
      </div>

      <div className="border-t-2 border-gray-200 pt-6">
        <h2 className="text-2xl font-bold text-mmj-header mb-4">
          Sign and submit
        </h2>
        <p className="text-base md:text-lg text-gray-700 mb-4">
          By typing your full legal name below and checking the agreement box,
          you are signing this intake form electronically.
        </p>
        <TypedSignature
          value={signedName}
          onChange={setSignedName}
          attested={attested}
          onAttestedChange={setAttested}
          defaultName={defaultName}
        />
      </div>

      {/* Spacer above the sticky submit footer so the last clause isn't hidden behind it on mobile. */}
      <div className="h-2" />
    </div>
  );
};

export default ConsentSection;
